/**
 * UI language — the twin of `ui/theme.ts`: a per-browser preference kept in
 * localStorage, applied to `<html lang>`, with a toggle button rendered next to the
 * theme one.
 *
 * English is the default and the source of truth: every key's `en` string is the text
 * that used to be hardcoded in the view, and a key with no `ru` translation falls back
 * to it rather than showing a raw key. So an untranslated string degrades to English —
 * the panel's previous behaviour — instead of breaking the screen.
 *
 * Switching reloads the page. The SPA renders its views imperatively into `innerHTML`
 * (there is no reactive layer to re-run), so re-translating the live DOM would mean
 * re-rendering every open page, modal and popover from here; a reload rebuilds all of
 * it through the normal path, and the router restores the current route from the URL.
 */
import { LANG_STRINGS } from './lang-strings';
export const LANGS = ['en', 'ru'];
const LANG_STORAGE_KEY = 'panel-lang';
/** Not the browser's `navigator.language`: the panel is English-first by product decision,
 *  and a Russian-locale browser must not silently switch an operator's panel for them. */
const DEFAULT_LANG = 'en';
function isLang(value) {
    return LANGS.includes(value);
}
function getPreferredLang() {
    try {
        const saved = localStorage.getItem(LANG_STORAGE_KEY);
        if (isLang(saved))
            return saved;
    }
    catch {
        // localStorage may be unavailable (private mode / blocked cookies) — use English.
    }
    return DEFAULT_LANG;
}
let currentLang = getPreferredLang();
export function getCurrentLang() {
    return currentLang;
}
/**
 * The UI string for `key` in the current language.
 *
 * `params` fill `{name}` placeholders. Values are inserted verbatim — `t()` does no
 * escaping, exactly like the template literals it replaces, so an interpolated value
 * that reaches innerHTML must still be passed through `esc()` by the caller.
 *
 * An unknown key returns the key itself: visible in the UI (so it gets reported) but
 * harmless, and never an empty label.
 */
export function t(key, params) {
    const entry = LANG_STRINGS[key];
    let text = entry ? (entry[currentLang] || entry.en) : key;
    if (params) {
        for (const [name, value] of Object.entries(params)) {
            text = text.split(`{${name}}`).join(String(value));
        }
    }
    return text;
}
function getLangLabel(lang) {
    // The button shows the CURRENT language, not the one a click switches to: a two-letter
    // code reads as a state badge, so "RU" on an English panel looks like the panel is
    // already Russian. (The theme toggle can show its target because a sun/moon is a
    // metaphor, not a label.) What the click does is in the tooltip, written in the
    // language currently on screen.
    return lang === 'en'
        ? { title: 'Switch to Russian', text: 'EN' }
        : { title: 'Переключить на английский', text: 'RU' };
}
function syncLangButtons(lang) {
    const label = getLangLabel(lang);
    document.querySelectorAll('[data-lang-toggle]').forEach((btn) => {
        btn.textContent = label.text;
        btn.title = label.title;
        btn.setAttribute('aria-label', label.title);
        btn.dataset['lang'] = lang;
    });
}
export function applyLang(lang, persist = true) {
    currentLang = lang;
    document.documentElement.lang = lang;
    if (persist) {
        try {
            localStorage.setItem(LANG_STORAGE_KEY, lang);
        }
        catch {
            // Ignore storage failures — the language still applies to this page load.
        }
    }
    syncLangButtons(lang);
}
export function initLang() {
    const lang = getPreferredLang();
    applyLang(lang, false);
    return lang;
}
export function toggleLang() {
    const next = getCurrentLang() === 'en' ? 'ru' : 'en';
    applyLang(next);
    // See the module comment: the rendered DOM is not reactive, so re-read it from scratch.
    window.location.reload();
    return next;
}
export function createLangToggleButton(className = '') {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = ['lang-toggle-btn', className].filter(Boolean).join(' ');
    button.dataset['langToggle'] = '1';
    const lang = getCurrentLang();
    const label = getLangLabel(lang);
    button.textContent = label.text;
    button.title = label.title;
    button.setAttribute('aria-label', label.title);
    button.dataset['lang'] = lang;
    button.addEventListener('click', () => {
        toggleLang();
    });
    return button;
}
